import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {getHasLiveDashboardWaitingMetrics} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {FilterButton} from '../../../../libs/shared/ui/src/components/filters/filterButton';
import {FilterDropdownBox} from '../../../../libs/shared/ui/src/components/filters/filterDropdownBox';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {FlexSpacer} from '../../../components/common/flex';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {getInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {popoutLiveDashboardAction} from '../../../store/panels/analytics/analyticsPanelActions';
import {renderInboxButtonItem} from '../../helpers/analyticsFiltersHelpers';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from '../filters/common/analyticsFilterButtonValue';
import {useFiltersTracking} from '../filters/common/helpers';
import {useAnalyticsLiveDashboardWithWaitingFilter} from '../filters/useAnalyticsLiveDashboardWithWaitingFilter';
import {LIVE_DASHBOARD_MAX_WIDTH} from './constants';
import {LiveDashboardInboxesDropdown} from './liveDashboardInboxesDropdown';
import {LiveDashboardLastUpdatedAtTimer} from './liveDashboardLastUpdatedAtTimer';

/*
 * Props.
 */

interface LiveDashboardReportHeaderTitleRowProps {
  panelId: AnalyticsPanelId;
  namespace: string;
  reportFetchedAtMillis: number | undefined;
  isLiveDashboardPoppedOut: boolean;
}

/*
 * Style.
 */

const StyledTitleRowDiv = styled.div`
  align-self: stretch;

  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const StyledWrapperColumnDiv = styled.div`
  max-width: ${LIVE_DASHBOARD_MAX_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 24px;
  margin: auto;
`;

// TODO(ldemarne): DRY with Workload report header.
const StyledReportTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};

  // Not supported in fontSizes currently
  font-size: 24px;
  font-weight: ${fontWeights.black};
`;

const StyledInboxFilterDiv = styled.div`
  max-width: 300px;
`;

const StyledFiltersRowDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

/*
 * Component.
 */

// Similar to ReportHeaderTitleRow, with changes made for Live Dashboard implementation
export const LiveDashboardHeader: FC<LiveDashboardReportHeaderTitleRowProps> = ({
  panelId,
  namespace,
  reportFetchedAtMillis,
  isLiveDashboardPoppedOut,
}) => {
  const allInboxes = useAppSelector(getInboxes);
  const dispatch = useAppDispatch();
  const getPanelId = useMemo(() => () => panelId, [panelId]);

  const {trackOnEdit} = useFiltersTracking(AnalyticsFilterTypesEnum.LIVE_DASHBOARD_INBOXES, getPanelId);
  const [selectedInboxesValue, setSelectedInboxesValue] = useAnalyticsFilter(
    AnalyticsFilterTypesEnum.LIVE_DASHBOARD_INBOXES,
    getPanelId,
  );
  const {
    renderButton: renderWaitingButton,
    isActive: isWaitingActive,
    setIsActive: setIsWaitingActive,
  } = useAnalyticsLiveDashboardWithWaitingFilter(getPanelId);
  const hasWaitingMetricsFeature = useAppSelector(getHasLiveDashboardWaitingMetrics);

  const selectedInboxIds = selectedInboxesValue ?? [];
  const hasSelectedInboxes = selectedInboxIds.length > 0;

  const setSelectedInboxesValueAndTrack = (selection: ReadonlyArray<number>) => {
    trackOnEdit(selection, selectedInboxIds);
    setSelectedInboxesValue(selection);
  };

  const renderRightButton = () => {
    // We don't want to allow popping out multiple Live dashboards.
    if (isLiveDashboardPoppedOut) {
      return null;
    }

    return (
      <Button
        variant={ButtonVariantsEnum.SECONDARY}
        rightIcon="externalLinkSmall"
        onClick={() => {
          dispatch(popoutLiveDashboardAction({panelId}));
        }}
      >
        <Trans id="analytics-live-dashboard-expand">Expand</Trans>
      </Button>
    );
  };

  return (
    <StyledWrapperColumnDiv>
      <StyledTitleRowDiv>
        <StyledReportTitleDiv>
          <Trans id="analytics-live-dashboard-header-title">Live dashboard</Trans>
        </StyledReportTitleDiv>
        {isDefined(reportFetchedAtMillis) && (
          <LiveDashboardLastUpdatedAtTimer reportFetchedAtMillis={reportFetchedAtMillis} />
        )}
        <FlexSpacer />
        {renderRightButton()}
      </StyledTitleRowDiv>
      <StyledFiltersRowDiv>
        <StyledInboxFilterDiv>
          <FilterDropdownBox
            isActive={hasSelectedInboxes}
            renderButton={() => (
              <AnalyticsFilterButtonValue
                iconName={hasSelectedInboxes ? 'inboxFilledSmall' : 'inboxSmall'}
                selectedIds={selectedInboxIds}
                itemsMap={allInboxes}
                label={<Trans id="analytics-inboxes-selection-dropdown-inner-label">Select inboxes</Trans>}
                renderItem={renderInboxButtonItem}
                shouldLabelSelection
                isActive={hasSelectedInboxes}
                shouldShrink
              />
            )}
            renderDropdown={(popoverProps) => (
              <LiveDashboardInboxesDropdown
                namespace={namespace}
                popoverProps={popoverProps}
                selectedInboxIds={selectedInboxIds}
                setSelectedInboxIds={setSelectedInboxesValueAndTrack}
              />
            )}
          />
        </StyledInboxFilterDiv>
        {hasWaitingMetricsFeature && (
          <FilterButton isActive={isWaitingActive} onClick={() => setIsWaitingActive(!isWaitingActive)}>
            {renderWaitingButton()}
          </FilterButton>
        )}
      </StyledFiltersRowDiv>
    </StyledWrapperColumnDiv>
  );
};
